import { ComponentKeys } from "./component-service-base";
import type { componentID, modes, Component } from "./component-service-base";
import rawCommonAdapterNameData from "../../../resources/common-adapter-names.json";
import APPTYPES from "../../../src/modules/apps/pages/create-app-page-templates/services/create-app-page-app-types.const.json";
import {
    commonAdapterId,
    commonAdapterData,
    CommonAdapterNameKeys,
    CommonAdapterNameServiceType
} from "./Types/common-adapter-name-service-types";

/**
 * Components mapped as a typed record for safer access.
 */
const commonAdapterNameData = (rawCommonAdapterNameData as unknown) as Record<commonAdapterId, commonAdapterData>;

/**
 * SERVICE
 *
 * Service for accessing common name mapping metadata.
 */
class CommonAdapterNameService implements CommonAdapterNameServiceType {
    /**
     * Returns an array of all registered component IDs.
     */
    getCommonNames() {
        return Object.keys(commonAdapterNameData) as Array<commonAdapterId>;
    }

    getILComponentName(commonName:string): componentID {
        return commonAdapterNameData[commonName]?.[CommonAdapterNameKeys.IL] as componentID;
    }

    getCDCComponentName(commonName:string): componentID {
        return commonAdapterNameData[commonName]?.[CommonAdapterNameKeys.CDC] as componentID;
    }

    /**
     * Returns the component name of the specified app type.
     * Basically used to generate URL for wizards
     *
     * @param commonName
     * Need to pass commonName. Value should either be one of key in adapter-mapping.json.
     * @param appType
     *  Need to pass appType. App should either IL | CDC.
     *
     */
    getComponentName(commonName: componentID, appType: string): componentID | null {
        const clubbedAdapterInfo = commonAdapterNameData[commonName];
        if (!clubbedAdapterInfo) {
            return null;
        }

        if (appType === APPTYPES.AUTOMATEDILCDC) appType = APPTYPES.IL;
        return clubbedAdapterInfo[appType] as componentID;
    }

    /**
     * It clubs IL and CDC modes.
     * Example :
     *  IL -> [ IL, ILCDC]
     *  CDC -> [CDC]
     *  OUTPUT -> [ IL, ILCDC, CDC]
     *
     * @param ILSupportedMode
     * Need to pass all the IL supported modes.
     * @param CDCSupportedMode
     * Need to pass all the CDC supported modes.
     */
    private clubModes(ILSupportedMode: modes = [], CDCSupportedMode: modes = []): modes {
        if (!ILSupportedMode && !CDCSupportedMode) {
            return [];
        }
        return [...new Set([...ILSupportedMode, ...CDCSupportedMode])] as modes;
    }

    /**
     * This clubs the IL and CDC adapters into single adapter
     *
     * @param adapterList
     * Need to pass the adapter list that needs to be clubbed.
     */
    clubAdapters(adapterList: Record<componentID, Component>): void {
        const componentNames = Object.keys(adapterList);

        Object.values(commonAdapterNameData).forEach(component => {
            const ILComponentName = component[CommonAdapterNameKeys.IL];
            const CDCComponentName = component[CommonAdapterNameKeys.CDC];
            const duplicateAdapter = component[CommonAdapterNameKeys.DUPLICATE];


            const shouldBeClubbed =
                componentNames.includes(ILComponentName) && componentNames.includes(CDCComponentName);

            if (shouldBeClubbed) {
                const ILAdapterInfo = adapterList[ILComponentName];
                const CDCAdapterInfo = adapterList[CDCComponentName];
                const clubbedAdapterInfo = Object.assign({}, ILAdapterInfo, CDCAdapterInfo);

                const uniqueClubbedMode = this.clubModes(ILAdapterInfo.Mode, CDCAdapterInfo.Mode);

                adapterList[component[CommonAdapterNameKeys.COMMONNAME]] = {
                    ...clubbedAdapterInfo,
                    [ComponentKeys.ComponentName]: component[CommonAdapterNameKeys.COMMONNAME],
                    [ComponentKeys.HumanReadableName]: component[CommonAdapterNameKeys.COMMONNAME],
                    [ComponentKeys.NewlyReleased]:
                        ILAdapterInfo[ComponentKeys.NewlyReleased] || CDCAdapterInfo[ComponentKeys.NewlyReleased],
                    [ComponentKeys.Mode]: uniqueClubbedMode,
                    // Preserve each underlying modes component, adapter info
                    [ComponentKeys.ILModeInfo]: {
                        componentName: ILComponentName,
                        adapter: ILAdapterInfo[ComponentKeys.Adapter]
                    },
                    [ComponentKeys.CDCModeInfo]: {
                        componentName: CDCComponentName,
                        adapter: CDCAdapterInfo[ComponentKeys.Adapter]
                    }
                };

                delete adapterList[ILComponentName];
                delete adapterList[CDCComponentName];
                delete adapterList[duplicateAdapter];

            }
        });
    }
}

/**
 * Singleton instance of the ComponentService.
 */
const commonAdapterNameService = new CommonAdapterNameService();
export default commonAdapterNameService;
